import { createContext, useCallback, useContext, useState } from 'react';
import { CanvasContext } from '@/contexts/CanvasContext';

interface AuiSurfaceContextValue {
  runId?: string;
  surfaceId?: string;
}

export const AuiSurfaceContext = createContext<AuiSurfaceContextValue>({});

export function useAuiSurface() {
  return useContext(AuiSurfaceContext);
}

interface UiStateSlice<K extends string, T> {
  kind: K;
  v: number;
  data: T;
}

function isSlice<K extends string>(value: unknown, kind: K): value is UiStateSlice<K, unknown> {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as { kind?: unknown }).kind === kind &&
    'data' in (value as Record<string, unknown>)
  );
}

/**
 * Reads/writes a single component's slice of `surface.uiState`. The slice is
 * wrapped with a `kind` discriminator so a stale slice (agent reused a node id
 * for a different component type) is silently ignored — the caller gets
 * `defaultValue` instead of mismatched data.
 *
 * Falls back to plain component-local state when there is no surrounding
 * CanvasProvider or AuiSurface context (e.g. on the dev showcase page).
 * In that mode, changes persist for the component's lifetime only.
 */
export function useSurfaceNodeUiState<K extends string, T>(
  nodeId: string,
  kind: K,
  defaultValue: T,
): [T, (next: T) => void] {
  const { surfaceId } = useContext(AuiSurfaceContext);
  const canvasCtx = useContext(CanvasContext);
  const [local, setLocal] = useState<T>(defaultValue);

  let value: T = local;
  if (surfaceId && canvasCtx) {
    for (const s of canvasCtx.state.surfaces.values()) {
      if (s.surfaceId !== surfaceId) continue;
      const raw = s.uiState?.[nodeId];
      if (isSlice(raw, kind)) value = raw.data as T;
      break;
    }
  }

  const setValue = useCallback(
    (next: T) => {
      setLocal(next);
      if (surfaceId && canvasCtx) {
        canvasCtx.setSurfaceNodeUiState(surfaceId, nodeId, kind, next);
      }
    },
    [surfaceId, nodeId, kind, canvasCtx],
  );

  return [value, setValue];
}
